import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemSecondaryAction,
  Paper,
  Divider,
  Chip,
} from '@mui/material';
import {
  DirectionsCar,
  LocationOn,
  Phone,
} from '@mui/icons-material';
import { useSnackbar } from '../contexts/SnackbarContext';
import { rideAPI } from '../services/api';
import { wsService } from '../services/websocket';
import { RIDER_EVENTS } from '../constants/websocket-events';
import { transformBackendRideToFrontend } from '../store/rideStore';
import { normalizeRideStatus, RideStatus } from '../constants/ride-status';

interface RideData {
  id: string;
  status: string;
  driver: {
    id: string;
    name: string;
    avatar?: string;
    rating: number;
    car: {
      make: string;
      model: string;
      color: string;
      plateNumber: string;
    };
    phone?: string;
  };
  origin: {
    address: string;
    latitude: number;
    longitude: number;
  };
  destination: {
    address: string;
    latitude: number;
    longitude: number;
  };
  estimatedTime?: number;
  estimatedFare?: number;
  startTime?: string;
  endTime?: string;
  fare?: number;
}

const RideTrackingListPage: React.FC = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [rides, setRides] = useState<RideData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRides = useCallback(async () => {
      try {
        setLoading(true);
        setError(null);

        let allRides: any[] = [];


        try {
          const activeRideResponse = await rideAPI.getActiveRide();
          console.log('📦 Active ride response:', activeRideResponse);


          let activeRide = null;
          if (activeRideResponse) {
            if (activeRideResponse.success && activeRideResponse.data) {
              activeRide = activeRideResponse.data;
            } else if (activeRideResponse.data && !activeRideResponse.success) {

              activeRide = activeRideResponse.data;
            } else if (!activeRideResponse.data && activeRideResponse.success) {

              const { success, ...rest } = activeRideResponse;
              if (Object.keys(rest).length > 0) {
                activeRide = rest;
              }
            } else if (!activeRideResponse.success && !activeRideResponse.data) {

              activeRide = activeRideResponse;
            }
          }

          if (activeRide) {
            console.log('✅ Active ride found:', activeRide);
            allRides.push(activeRide);
          } else {
            console.log('⚠️ No active ride found');
          }
        } catch (activeRideError) {
          console.error('❌ Error fetching active ride:', activeRideError);

        }


        try {
          const historyResponse = await rideAPI.getRideHistory(1, 50);
          console.log('📦 History response:', historyResponse);

          let historyRides: any[] = [];


          if (historyResponse) {

            if (Array.isArray(historyResponse)) {
              historyRides = historyResponse;
            }

            else if (historyResponse.success && Array.isArray(historyResponse.data)) {
              historyRides = historyResponse.data;
            }

            else if (historyResponse.success && historyResponse.data && Array.isArray(historyResponse.data.data)) {
              historyRides = historyResponse.data.data;
            }

            else if (Array.isArray(historyResponse.data)) {
              historyRides = historyResponse.data;
            }

            else if (historyResponse.data && Array.isArray(historyResponse.data.data)) {
              historyRides = historyResponse.data.data;
            }

            else if (historyResponse.data && Array.isArray(historyResponse.data.rides)) {
              historyRides = historyResponse.data.rides;
            }
          }


          const filteredHistoryRides = historyRides.filter((ride: any) => {
            const rideId = ride.id?.toString() || String(ride.id);
            return !allRides.some((activeRide: any) => {
              const activeRideId = activeRide.id?.toString() || String(activeRide.id);
              return activeRideId === rideId;
            });
          });

          allRides = [...allRides, ...filteredHistoryRides];
          console.log('✅ Total rides found:', allRides.length);
        } catch (historyError) {
          console.error('❌ Error fetching ride history:', historyError);

        }


        const transformedRides = allRides.map((ride: any) => {
          try {
            const transformedRide = transformBackendRideToFrontend(ride);
            if (!transformedRide) {
              console.warn('⚠️ Failed to transform ride:', ride);
              return null;
            }

            const normalizedStatus = normalizeRideStatus(transformedRide.status);


            if (
              normalizedStatus === RideStatus.COMPLETED || 
              normalizedStatus === RideStatus.CANCELLED ||
              normalizedStatus === RideStatus.NO_DRIVERS_AVAILABLE
            ) {
              console.log('⏭️ Skipping ride:', normalizedStatus, transformedRide.id);
              return null;
            }

            return {
              id: transformedRide.id.toString(),
              status: normalizedStatus.toString(),
              driver: transformedRide.driver ? {
                id: transformedRide.driver.id?.toString() || transformedRide.driver_id || '',
                name: transformedRide.driver.name || transformedRide.driver.full_name || 'Sürücü',
                avatar: transformedRide.driver.avatar || transformedRide.driver.profile_picture,
                rating: transformedRide.driver.rating || transformedRide.driver.average_rating || 0,
                car: {
                  make: transformedRide.driver.vehicle?.make || transformedRide.driver.car?.make || '',
                  model: transformedRide.driver.vehicle?.model || transformedRide.driver.car?.model || '',
                  color: transformedRide.driver.vehicle?.color || transformedRide.driver.car?.color || '',
                  plateNumber: transformedRide.driver.vehicle?.plate_number || transformedRide.driver.car?.plateNumber || '',
                },
                phone: transformedRide.driver.phone || transformedRide.driver.phone_number,
              } : {
                id: '',
                name: 'Gözlənilir...',
                rating: 0,
                car: { make: '', model: '', color: '', plateNumber: '' },
              },
              origin: {
                address: transformedRide.start_point?.address || transformedRide.origin_address || '',
                latitude: transformedRide.start_point?.latitude || 0,
                longitude: transformedRide.start_point?.longitude || 0,
              },
              destination: {
                address: transformedRide.end_point?.address || transformedRide.destination_address || '',
                latitude: transformedRide.end_point?.latitude || 0,
                longitude: transformedRide.end_point?.longitude || 0,
              },
              estimatedFare: transformedRide.estimated_fare,
              fare: transformedRide.ride_fare,
              startTime: transformedRide.started_at,
              endTime: transformedRide.completed_at,
            };
          } catch (transformError) {
            console.error('❌ Error transforming ride:', transformError, ride);
            return null;
          }
        }).filter(Boolean) as RideData[];

        console.log('✅ Active rides found (after filtering):', transformedRides.length);
        setRides(transformedRides);
      } catch (err: any) {
        console.error('❌ Error in fetchRides:', err);
        const errorMessage = err?.response?.data?.message || err?.message || 'Sifarişlər yüklənə bilmədi';
        setError(errorMessage);
        showSnackbar(errorMessage, 'error');
      } finally {
        setLoading(false);
      }
  }, [showSnackbar]);

  useEffect(() => {
    fetchRides();


    const setupWebSocket = async () => {
      try {
        await wsService.ensureConnection('rider');

        const handleRideCreated = async (data: any) => {
          console.log('🔵 Ride created event received in RideTrackingListPage:', data);

          await fetchRides();
        };

        const handleRideCancelled = async (data: any) => {
          console.log('🔵 Ride cancelled event received in RideTrackingListPage:', data);
          const rideData = data.ride || data.data || data;
          const rideId = rideData?.id || data.ride_id || data.rideId || data.id;

          if (rideId) {
            const rideIdString = String(rideId);

            setRides(prevRides => prevRides.filter(r => r.id !== rideIdString));
            console.log('Removed cancelled ride from list:', rideIdString);
          } else {

            await fetchRides();
          }
        };

        const handleRideUpdated = async (data: any) => {
          console.log('🔵 Ride updated event received in RideTrackingListPage:', data);


          const rideData = data.ride || data.data || data;

          if (!rideData || !rideData.id) {
            console.warn('⚠️ Invalid ride update data:', data);

            await fetchRides();
            return;
          }

          const transformedRide = transformBackendRideToFrontend(rideData);
          if (!transformedRide) {
            console.warn('⚠️ Failed to transform ride update:', rideData);
            return;
          }

          const normalizedStatus = normalizeRideStatus(transformedRide.status);
          const statusString = normalizedStatus.toString();


          if (
            statusString === RideStatus.COMPLETED || 
            statusString === RideStatus.CANCELLED ||
            statusString === RideStatus.NO_DRIVERS_AVAILABLE
          ) {

            setRides(prevRides => prevRides.filter(r => r.id !== transformedRide.id.toString()));
            return;
          }

          setRides(prevRides => {
            const rideId = transformedRide.id.toString();
            const existingRideIndex = prevRides.findIndex(r => r.id === rideId);

            const updatedRide: RideData = {
              id: rideId,
              status: normalizedStatus.toString(),
              driver: transformedRide.driver ? {
                id: transformedRide.driver.id?.toString() || transformedRide.driver_id || '',
                name: transformedRide.driver.name || transformedRide.driver.full_name || 'Sürücü',
                avatar: transformedRide.driver.avatar || transformedRide.driver.profile_picture,
                rating: transformedRide.driver.rating || transformedRide.driver.average_rating || 0,
                car: {
                  make: transformedRide.driver.vehicle?.make || transformedRide.driver.car?.make || '',
                  model: transformedRide.driver.vehicle?.model || transformedRide.driver.car?.model || '',
                  color: transformedRide.driver.vehicle?.color || transformedRide.driver.car?.color || '',
                  plateNumber: transformedRide.driver.vehicle?.plate_number || transformedRide.driver.car?.plateNumber || '',
                },
                phone: transformedRide.driver.phone || transformedRide.driver.phone_number,
              } : {
                id: '',
                name: 'Gözlənilir...',
                rating: 0,
                car: { make: '', model: '', color: '', plateNumber: '' },
              },
              origin: {
                address: transformedRide.start_point?.address || transformedRide.origin_address || '',
                latitude: transformedRide.start_point?.latitude || 0,
                longitude: transformedRide.start_point?.longitude || 0,
              },
              destination: {
                address: transformedRide.end_point?.address || transformedRide.destination_address || '',
                latitude: transformedRide.end_point?.latitude || 0,
                longitude: transformedRide.end_point?.longitude || 0,
              },
              estimatedFare: transformedRide.estimated_fare,
              fare: transformedRide.ride_fare,
              startTime: transformedRide.started_at,
              endTime: transformedRide.completed_at,
            };

            if (existingRideIndex >= 0) {

              const updatedRides = [...prevRides];
              updatedRides[existingRideIndex] = updatedRide;

              const activeRides = updatedRides.filter(r => 
                r.status !== 'COMPLETED' && r.status !== 'CANCELLED'
              );
              const completedRides = updatedRides.filter(r => 
                r.status === 'COMPLETED' || r.status === 'CANCELLED'
              );
              return [...activeRides, ...completedRides];
            } else {

              if (
                normalizedStatus !== RideStatus.COMPLETED && 
                normalizedStatus !== RideStatus.CANCELLED &&
                normalizedStatus !== RideStatus.NO_DRIVERS_AVAILABLE
              ) {
                return [updatedRide, ...prevRides];
              } else {

                return prevRides;
              }
            }
          });
        };

        wsService.on(RIDER_EVENTS.RIDE_CREATED, handleRideCreated);
        wsService.on(RIDER_EVENTS.RIDE_UPDATED, handleRideUpdated);
        wsService.on(RIDER_EVENTS.RIDE_STATUS_CHANGED, handleRideUpdated);
        wsService.on(RIDER_EVENTS.RIDE_CANCELLED, handleRideCancelled);
        wsService.on(RIDER_EVENTS.CURRENT_RIDE, handleRideCreated);


        wsService.on('ride:created', handleRideCreated);
        wsService.on('ride:cancelled', handleRideCancelled);
        wsService.on('current_ride', handleRideCreated);

        return () => {
          wsService.off(RIDER_EVENTS.RIDE_CREATED, handleRideCreated);
          wsService.off(RIDER_EVENTS.RIDE_UPDATED, handleRideUpdated);
          wsService.off(RIDER_EVENTS.RIDE_STATUS_CHANGED, handleRideUpdated);
          wsService.off(RIDER_EVENTS.RIDE_CANCELLED, handleRideCancelled);
          wsService.off(RIDER_EVENTS.CURRENT_RIDE, handleRideCreated);
          wsService.off('ride:created', handleRideCreated);
          wsService.off('ride:cancelled', handleRideCancelled);
          wsService.off('current_ride', handleRideCreated);
        };
      } catch (error) {
        console.error('WebSocket setup error:', error);
      }
    };

    setupWebSocket();


    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchRides();
      }
    };

    const handleWindowFocus = () => {
      fetchRides();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [showSnackbar, fetchRides]);

  const getStatusText = (status: string): string => {
    const normalizedStatus = status.toUpperCase();
    switch (normalizedStatus) {
      case 'PENDING':
        return 'Gözləyir';
      case 'SEARCHING_DRIVER':
        return 'Sürücü axtarılır';
      case 'DRIVER_ASSIGNED':
      case 'ACCEPTED':
        return 'Sürücü təyin edildi';
      case 'DRIVER_ARRIVED':
      case 'ARRIVING':
        return 'Sürücü gəldi';
      case 'STARTED':
      case 'IN_PROGRESS':
        return 'Səyahət davam edir';
      case 'COMPLETED':
        return 'Səyahət bitdi';
      case 'CANCELLED':
        return 'Sifariş ləğv edildi';
      case 'NO_DRIVERS_AVAILABLE':
        return 'Sürücü tapılmadı';
      default:
        return status;
    }
  };

  const getStatusColor = (status: string): string => {
    const normalizedStatus = status.toUpperCase();
    switch (normalizedStatus) {
      case 'PENDING':
      case 'SEARCHING_DRIVER':
        return 'warning';
      case 'DRIVER_ASSIGNED':
      case 'ACCEPTED':
        return 'info';
      case 'DRIVER_ARRIVED':
      case 'ARRIVING':
        return 'primary';
      case 'STARTED':
      case 'IN_PROGRESS':
        return 'success';
      case 'COMPLETED':
        return 'success';
      case 'CANCELLED':
      case 'NO_DRIVERS_AVAILABLE':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          Sifarişlərim
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Sifarişlərinizi izləyin və idarə edin
        </Typography>
      </Box>

      {rides.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <DirectionsCar sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Hələlik sifarişiniz yoxdur
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Yeni sifariş vermək üçün "Yeni Sifariş" düyməsinə basın
          </Typography>
        </Box>
      ) : (
        <List>
          {rides.map((ride) => (
            <Paper key={ride.id} sx={{ mb: 2, borderRadius: 2 }}>
              <ListItem>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Chip
                        label={getStatusText(ride.status)}
                        color={getStatusColor(ride.status) as any}
                        size="small"
                        sx={{ mr: 2 }}
                      />
                      <Typography variant="subtitle1" fontWeight="bold">
                        Sifariş №{ride.id.slice(-8)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LocationOn sx={{ color: 'text.secondary', mr: 1, fontSize: 16 }} />
                      <Typography variant="body2" color="text.secondary">
                        {ride.origin.address}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LocationOn sx={{ color: 'text.secondary', mr: 1, fontSize: 16 }} />
                      <Typography variant="body2" color="text.secondary">
                        {ride.destination.address}
                      </Typography>
                    </Box>

                    {ride.driver && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Sürücü: {ride.driver.name} ({ride.driver.car.make} {ride.driver.car.model})
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LocationOn sx={{ color: 'text.secondary', mr: 1, fontSize: 16 }} />
                      <Typography variant="body2" color="text.secondary">
                        {ride.startTime ? new Date(ride.startTime).toLocaleString() : 'N/A'} - {ride.endTime ? new Date(ride.endTime).toLocaleString() : 'N/A'}
                      </Typography>
                    </Box>
                  </Box>

                  <ListItemSecondaryAction>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<DirectionsCar />}
                      onClick={() => navigate(`/app/track/${ride.id}`)}
                      sx={{ mr: 1 }}
                    >
                      İzlə
                    </Button>

                    {ride.status !== 'COMPLETED' && ride.status !== 'CANCELLED' && ride.driver?.phone && (
                      <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<Phone />}
                        onClick={() => window.location.href = `tel:${ride.driver.phone}`}
                      >
                        Zəng et
                      </Button>
                    )}
                  </ListItemSecondaryAction>
                </Box>
              </ListItem>
              <Divider />
            </Paper>
          ))}
        </List>
      )}
    </Box>
  );
};

export default RideTrackingListPage;
